import { useCallback } from "react";
import { t } from "i18next";
import { uploadVideo } from "@apis/uploadVideo";
import type { ClipUploadContext, ClipUploadFile } from "@components/ClipUpload/ClipUpload";
import { generateUid } from "@components/ClipUpload/utils";
import { isNotNil } from "@kalodata/tools";
import { MAX_VIDEO_FILE_BYTES, VIDEO_FILE_ACCEPT } from "./constants";

const getFileExtension = (fileName: string) => {
  const index = fileName.lastIndexOf(".");
  return index >= 0 ? fileName.slice(index).toLowerCase() : "";
};

const isAllowedVideoFile = (file: File) => {
  const extension = getFileExtension(file.name);
  const allowedExtensions = [".mp4", ".mov"];
  if (allowedExtensions.includes(extension)) return true;
  return VIDEO_FILE_ACCEPT.some((item) => item.startsWith("video/") && file.type === item);
};

export const usePostVideoUpload = () => {
  const beforeUpload = useCallback((file: File) => {
    if (!isAllowedVideoFile(file)) {
      return t("File too large or format unsupported,please try again");
    }
    if (file.size > MAX_VIDEO_FILE_BYTES) {
      return t("File too large or format unsupported,please try again");
    }
    return true;
  }, []);

  const uploadFile = useCallback(async (file: File, ctx: ClipUploadContext): Promise<ClipUploadFile> => {
    const result = await uploadVideo(file, ctx.onProgress, ctx.signal);
    return {
      uid: isNotNil(result?.assetId) ? String(result.assetId) : generateUid(),
      name: file.name,
      url: result?.videoUrl,
      type: file.type,
      size: file.size,
      response: result,
    };
  }, []);

  return { beforeUpload, uploadFile, accept: [...VIDEO_FILE_ACCEPT] };
};
